import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { dropdownStyles } from '@/lib/dropdownStyles';

export interface LayoutModeOption {
  value: string;
  label: string;
  icon: JSX.Element;
}

export interface LayoutModeSelectorProps {
  value: string;
  options: LayoutModeOption[];
  onChange: (value: string) => void;
  'aria-label'?: string;
}

/**
 * Layout mode selector with icon trigger and dropdown menu
 * Used in footer for view mode selection (List, Grid, Magazine, etc.)
 */
export function LayoutModeSelector({
  value,
  options,
  onChange,
  'aria-label': ariaLabel = 'View layout selector',
}: LayoutModeSelectorProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const currentOption = options.find((opt) => opt.value === value);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          className="flex items-center gap-2 px-3 py-1.5 rounded hover:bg-surface-2 transition-colors text-foreground"
          aria-label={ariaLabel}
          title={`View layout: ${currentOption?.label || 'Select layout'}`}
        >
          {currentOption?.icon}
          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className={dropdownStyles.content} sideOffset={5} align="end">
          {options.map((option) => (
            <DropdownMenu.Item
              key={option.value}
              className={dropdownStyles.item}
              onSelect={() => onChange(option.value)}
            >
              <div className="flex items-center gap-2">
                {option.icon}
                <span>{option.label}</span>
              </div>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
